<template>
  <transition
    name="editor-drawer-transition"
    enter-from-class="editor-drawer-transition--enter-from"
    enter-active-class="editor-drawer-transition--enter-active"
    leave-active-class="editor-drawer-transition--leave-active"
    leave-to-class="editor-drawer-transition--leave-to"
  >
    <div
      v-show="notesOpen"
      class="notes-canvas__wrapper"
      :class="{
        'notes-canvas__wrapper--open': notesOpen,
        'notes-canvas__wrapper--wide': useWideNotes,
      }"
    >
      <div
        ref="scrollWrap"
        :aria-hidden="(!notesOpen).toString()"
        class="editor-drawer__scroll-wrap notes-canvas__scroll-wrap"
        :class="{
          'notes-canvas__scroll-wrap--edit-mode': mode === 'EDIT',
          'notes-canvas__scroll-wrap--wide': useWideNotes,
        }"
      >
        <div class="editor-drawer__heading-wrapper">
          <NebulaIcon
            class="editor-drawer__heading-icon"
            symbol-id="notes"
            size="m"
          />

          <h2 class="editor-drawer__heading">
            {{ useWideNotes ? $t('Educator Notes') : $t('Teacher Notes') }}
          </h2>

          <NebulaButton
            ref="closeButton"
            class="editor-drawer__toggle-button editor-drawer__toggle-button--notes"
            :aria-label="$t('Close Educator Notes')"
            :disabled="!notesOpen"
            icon-left="x"
            size="s"
            type="flat"
            @click="toggleDrawer"
          />
        </div>
        <div
          class="notes-canvas__empty-state"
          v-if="!copiedModules.length && mode === 'EDIT'"
        >
          {{ $t('Add teacher-only notes and content by clicking the button below.') }}
        </div>
        <div class="notes-canvas__modules-scroll-wrap">
          <div
            @paste="pasteEvent"
            class="nebula-screenreader-only"
          />
          <FullscreenNotesWide
            v-if="useWideNotes"
            :page="page"
            :page-index="pageIndex"
          />
          <GridLayout
            v-else-if="page && page.id && copiedModules.length || this.isPasted"
            class="notes-canvas"
            :class="{
              'notes-canvas--placeholder-hidden': placeholderHidden,
            }"
            :col-num="1"
            :row-height="32"
            :is-draggable="mode === 'EDIT'"
            :is-resizable="mode === 'EDIT'"
            :margin="[16, 16]"
            :layout="copiedModuleLayout"
            :use-css-transforms="false"
            ref="gridLayout"
            :auto-size="true"
            @layout-updated="onGridLayoutUpdated($event, notes, 'notes')"
          >
            <template
              v-for="{ mod, layout } in copiedModules"
              :key="mod.id"
            >
              <div
                v-if="mode === 'EDIT'"
                v-show="mod.id === activeModule"
                ref="gridItemBeforeContainers"
                :id="`grid-item-before-container-${mod.id}`"
                class="
                  grid-canvas__grid-item-before-container
                  grid-canvas__grid-item-before-container--fixed
                "
              />
              <GridItem
                :x="layout.x"
                :y="layout.y"
                :w="layout.w"
                :h="layout.h"
                :i="mod.id"
                @move="onMove"
                @resize="onResize"
                :is-resizable="!isLocked(mod.id) && mode === 'EDIT'"
                :is-resizable-y="!isLocked(mod.id) && !autoSizeShrinkEnabled(mod, 'notes')"
                :is-draggable="!isLocked(mod.id) && mode === 'EDIT'"
                :id="mod.id"
                :drag-ignore-from="EDITOR_MODULE_IGNORE_DRAG_SELECTOR"
                ref="gridItems"
                :class="[
                  {
                    'vue-grid-item--active': mod.id === activeModule,
                    'vue-grid-item--preview': mod.type === 'preview',
                  },
                ]"
              >
                <ModuleWrapper
                  :active="mod.id === activeModule"
                  :mode="mode"
                  :module-data="mod"
                  :module-name="moduleTypeToLabel(mod.type)"
                  canvas-name="notes"
                  :id="mod.id"
                  :get-after-module-container-ref="getAfterModuleContainerRef"
                  :module-animations-enabled="moduleAnimationsEnabled"
                >
                  <component
                    :is="moduleTypeToComponent(mod.type)"
                    :active="mod.id === activeModule"
                    :id="mod.id"
                    :mode="mode"
                    :module-data="mod"
                    :layout-data="layout"
                    :get-before-module-container-ref="getBeforeModuleContainerRef"
                    :page="page"
                    canvas-name="notes"
                  />
                  <template #module-options>
                    <component
                      :is="moduleTypeToOptions(mod.type)"
                      :id="mod.id"
                      :module-data="mod"
                      canvas-name="notes"
                    />
                  </template>
                </ModuleWrapper>
                <template v-if="mode === 'EDIT' && !isLocked(mod.id)">
                  <GridItemDragHandle
                    position="top"
                    :mod-type="mod.type"
                  />
                  <GridItemDragHandle
                    position="end"
                    :mod-type="mod.type"
                  />
                  <GridItemDragHandle
                    position="bottom"
                    :mod-type="mod.type"
                  />
                  <GridItemDragHandle
                    position="start"
                    :mod-type="mod.type"
                  />
                </template>
              </GridItem>
              <div
                v-if="mode === 'EDIT'"
                v-show="mod.id === activeModule"
                ref="gridItemAfterContainers"
                :id="`grid-item-after-container-${mod.id}`"
                class="grid-canvas__grid-item-after-container"
              />
            </template>
          </GridLayout>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { NebulaButton, NebulaIcon } from '@discoveryedu/nebula-components';
import interact from 'interactjs';
import { mapActions, mapState } from 'pinia';
import { GridLayout, GridItem } from '@discoveryedu/vue-grid-layout';
import { cloneDeep, get } from 'lodash-es';
import { moduleTypeToComponent, moduleTypeToOptions, moduleTypeToLabel } from '@/lib/utils';
import { EDITOR_CANVAS_GRID_MARGIN, EDITOR_MODULE_IGNORE_DRAG_SELECTOR } from '@/lib/constants';
import GridItemDragHandle from '@/components/GridItemDragHandle.vue';
import ModuleWrapper from '@/components/ModuleWrapper.vue';
import FullscreenNotesWide from '@/components/FullscreenNotesWide.vue';
import modules from '@/components/modules/index';
import * as types from '@/lib/constants/store';
import { bus } from '@/lib/eventBus';
import chain from '@/lib/utils/chain';
import { formatModuleLayoutForGrid } from '@/lib/utils/grid';
import canvas from '@/mixins/canvas';
import autoSizeEnabled from '@/mixins/autoSizeEnabled';
import {
  useEditorStore,
} from '@/stores';

export default {
  components: {
    FullscreenNotesWide,
    GridItem,
    GridLayout,
    ModuleWrapper,
    NebulaButton,
    NebulaIcon,
    GridItemDragHandle,
    ...modules, // merge dynamic components
  },
  mixins: [canvas, autoSizeEnabled],
  computed: {
    ...mapState(useEditorStore, {
      connectedUser: 'currentUserConnection',
    }),
    ...mapState(useEditorStore, [
      'currentPage',
      'currentlyEditing',
      'isLocked',
      'isCurrentPageHost',
      'pagesSorted',
      'editorDrawers',
      'editorIsFullscreen',
      'session',
    ]),
    page() {
      return this.pageId
        ? this.pagesSorted?.find((p) => p.id === this.pageId)
        : this.currentPage;
    },
    pageIndex() {
      return (this.pagesSorted || []).findIndex((p) => p.id === this.page?.id);
    },
    copiedModuleLayout() {
      return this.copiedModules.map((item) => item.layout);
    },
    notes() {
      return this.page.notes;
    },
    placeholderHidden() {
      // The placeholder module is always hidden in VIEW mode
      // and while dragging onto the canvas from the Add Module menu
      return this.mode === 'VIEW' || this.isDraggingFromAddModule;
    },
  },
  watch: {
    session() {
      this.$nextTick(() => {
        if (this.activeModule && !this.currentlyEditing(this.activeModule)) {
          this.activeModule = null;
        }
      });
    },
    isOpen() {
      this.$nextTick(() => {
        if (this.$parent.focusOpen) {
          this.$parent.focusOpen('notes');
        }
      });
    },
    notesOpen() {
      this.$nextTick(() => {
        this.$refs.closeButton.$el.focus();
      });
    },
    notes: {
      handler() {
        this.mapCopiedModulesFromStore();

        /*
          Anytime changes are made to the list of notes for the current page,
          the 'layout' prop for the GridLayout component will be updated with these
          changes.
          When two or more notes have overlapping coordinates or dimensions, we
          wait for the GridLayout component to resolve these conflicts internally.
          Then we get the resolved layout and push these changes back into Pinia
          and the SharedDocument so they propagate to other clients and are persisted
          to the server.
          Only the host user for the current page will commit resolved grid conflicts to avoid
          neverending cyclical updates between multiple clients.
        */
        if (this.isCurrentPageHost) {
          this.$nextTick(() => {
            const layout = get(this, '$refs.gridLayout.layout');
            if (layout) {
              this.onGridLayoutUpdated(layout, this.notes, 'notes', false);
            }
          });
        }
      },
      deep: true,
    },
    mode() {
      this.toggleMousedownHandler();
    },
  },
  created() {
    this.mapCopiedModulesFromStore();

    bus.on('initPlaceholderModule', this.onInitPlaceholderModule);
    bus.on('updateDraggableXY', this.onUpdateDraggableXY);
    bus.on('activeModule', this.onActiveModule);
    bus.on('module:added', this.onModuleAdded);
  },
  name: 'NotesCanvas',
  data() {
    return {
      activeModule: null,
      bus,
      copiedModules: [],
      colNum: 12,
      rowHeight: 32,
      gridMargin: [EDITOR_CANVAS_GRID_MARGIN, EDITOR_CANVAS_GRID_MARGIN],
      placeholderModule: {
        // We don't need an ID property because it will be assigned in Pinia action
        type: String,
        name: String,
        layout: Object,
        options: Object,
      },
      tempId: '00000000',
      x_position: 0,
      y_position: 0,
      isOpen: true,
      isDraggingFromAddModule: false,
      EDITOR_MODULE_IGNORE_DRAG_SELECTOR,
      isPasted: false,
    };
  },
  props: {
    mode: {
      type: String,
      default: 'VIEW', // out of ['EDIT', 'VIEW']
    },
    notesOpen: {
      type: Boolean,
      default: false,
    },
    menuContainer: {
      type: HTMLElement, // used in gridLayout mixin
      default: null,
    },
    moduleAnimationsEnabled: {
      type: Boolean,
      default: true,
    },
    useWideNotes: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      default: null,
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODULE,
      types.TOGGLE_EDITOR_DRAWER,
      types.UPDATE_CURRENTLY_EDITING,
    ]),
    moduleTypeToComponent,
    moduleTypeToOptions,
    moduleTypeToLabel,
    onBodyClick({ target }) {
      this.onGridElementClicked(target);
    },
    onModuleAdded(event) {
      if (event.canvasName === 'notes' && event.isLocal) this.scrollIntoView(event.id);
    },
    scrollIntoView(moduleId) {
      this.$nextTick(() => {
        const moduleEl = chain(this.$refs.gridItems)
          .find((item) => item.i === moduleId)
          .get('$el')
          .value();
        if (moduleEl) {
          const scrollWrapEl = this.$refs.scrollWrap;
          const scrollWrapRect = scrollWrapEl.getBoundingClientRect();
          const moduleRect = moduleEl.getBoundingClientRect();
          /*
            If the added module is outside the bounds of the scroll area,
            then bring it back into view. If it's above the scroll area,
            then align to top, otherwise align to the bottom.
          */
          if (moduleRect.bottom > scrollWrapRect.bottom) {
            moduleEl.scrollIntoView(false);
          } else if (moduleRect.top < scrollWrapRect.top) {
            moduleEl.scrollIntoView(true);
          }
        }
      });
    },
    setActive(id) {
      this.activeModule = id;
    },
    toggleDrawer() {
      this[types.TOGGLE_EDITOR_DRAWER]({
        drawer: this.editorIsFullscreen ? 'notesFullscreen' : 'notes',
      });
      this.isOpen = !this.isOpen;
    },
    onMove(moduleId) {
      this.resetDragging();
      bus.emit('module:move', moduleId);
    },
    onResize(moduleId) {
      this.resetDragging();
      bus.emit('module:resize', moduleId);
    },
    resetDragging() {
      this.isDraggingFromAddModule = false;
    },
    initDropZone() {
      const vm = this; // Current vue model, so we don't lose context in arrow functions
      let canvasEntered = null; // flag for drop area
      // Initialize drop zone and set up listeners
      const dropElement = this.$el.closest('.notes__contents');
      if (interact.isSet(dropElement)) {
        interact(dropElement).unset();
      }
      interact(dropElement).dropzone({
        accept: '.notes__contents .add-module__list-item--notes',
        ondropactivate() {
          vm.isDraggingFromAddModule = true;
        },
        ondragenter() {
          // switch the enter flag to true and initalize a position on the grid for the preview
          canvasEntered = 'notes';
          const placeholderLayout = {
            x_position: vm.x_position,
            y_position: vm.y_position,
            width: vm.placeholderModule.layout.width,
            height: vm.placeholderModule.layout.height,
          };
          vm.placeholderModule.layout = placeholderLayout;

          // Create a temporary module so the user can preview placement on grid
          const previewModule = vm.createPreviewModule(placeholderLayout);

          // When the preview begins, create a temporary module in the copiedModules list
          // to render the preview in the gridLayout
          bus.emit('beginPreview', { canvasEntered });
          vm.copiedModules.push({
            mod: previewModule,
            layout: formatModuleLayoutForGrid(previewModule),
          });
        },
        ondragleave() {
          // When the preview ends, delete the temporary module from the copiedModules list
          canvasEntered = null;
          vm.copiedModules = vm.copiedModules.filter((item) => item.mod.type !== 'preview');
        },
        ondrop() {
          // When the floating module is dropped, create a new module on the preview's position
          // and send it to the state
          vm.placeholderModule.layout = {
            x_position: vm.x_position,
            y_position: vm.y_position,
            height: vm.placeholderModule.layout.height,
            width: vm.placeholderModule.layout.width,
          };
          vm.addModuleToState(vm.placeholderModule);
          canvasEntered = null;
        },
        ondropdeactivate(event) {
          event.target.classList.remove('drop-active');
        },
      });
    },
    async addModuleToState(mod) {
      const newModuleId = await this[types.ADD_MODULE]({
        canvasName: 'notes',
        payload: mod,
      });
      this.setActive(newModuleId);
    },
    createPreviewModule(layout) {
      // Stub object
      return {
        id: this.tempId, // Temporary id
        type: 'preview',
        name: 'Preview',
        options: {},
        layout,
      };
    },
    toggleMousedownHandler() {
      /*
        Whenever mode changes, ensure the mousedown handler is
        attached or detached appropriately.
      */
      bus.off('document:mousedown', this.onBodyClick);
      if (this.mode === 'EDIT') {
        bus.on('document:mousedown', this.onBodyClick);
      }
    },
    onUpdateDraggableXY(data) {
      if (data.canvasName === 'notes') {
        this.x_position = data.x;
        this.y_position = data.y;
      }
    },
    onInitPlaceholderModule(data) {
      if (data.canvasName === 'notes') {
        this.placeholderModule.type = data.type;
        this.placeholderModule.name = data.name;
        this.placeholderModule.options = data.options;
        this.placeholderModule.layout = data.layout;
      }
    },
    onActiveModule(data) {
      if (data.canvasName === 'notes') {
        this.setActive(data.id);
        this[types.UPDATE_CURRENTLY_EDITING]({
          [data.id]: this.connectedUser?.id,
        });
      }
    },
    mapCopiedModulesFromStore() {
      this.copiedModules = cloneDeep(this.notes || []).map((mod) => ({
        mod,
        layout: formatModuleLayoutForGrid(mod),
      }));
    },
    pasteEvent() {
      this.isPasted = true;
    },
  },
  mounted() {
    this.toggleMousedownHandler();
    this.initDropZone();
  },
  beforeUnmount() {
    bus.off('document:mousedown', this.onBodyClick);
    bus.off('module:added', this.onModuleAdded);
    bus.off('updateDraggableXY', this.onUpdateDraggableXY);
    bus.off('initPlaceholderModule', this.onInitPlaceholderModule);
    bus.off('activeModule', this.onActiveModule);
    interact('.notes__contents').unset();
  },
};
</script>

<style lang="stylus">
.notes-canvas {
  margin-top: - $nebula-space-2x;

  &__wrapper {
    background-color: $nebula-color-white;
    flex-shrink: 0;
    height: 100%;
    position: relative;
    width: 300px;

    // Fullscreen wide notes have a different style than "normal" notes
    &--wide {
      width: 480px;

      .editor-drawer__heading-wrapper {
        align-items: flex-end;
        border-bottom: 1px solid $nebula-color-platform-neutral-400;
        height: 150px;
        margin: 0 $nebula-space-4x;
        padding: $nebula-space-4x 0 $nebula-space-3x;
      }

      .editor-drawer__heading-icon {
        display: block;
        fill: $nebula-color-platform-neutral-900;
        margin: 5px $nebula-space-1x; // align with heading text
      }

      .editor-drawer__heading {
        color: $nebula-color-platform-neutral-900;
        font-weight: normal;
        padding: 0;
      }

      .editor-drawer__toggle-button {
        align-self: flex-start;
        margin: 0;
      }
    }
  }

  &__scroll-wrap--wide.editor-drawer__scroll-wrap {
    width: 480px;
  }

  &__scroll-wrap--edit-mode {
    &.editor-drawer__scroll-wrap {
      bottom: 48px; // clear the bottom Add Menu
    }

    .notes-canvas {
      margin-bottom: $nebula-space-8x;
    }
  }

  &__modules-scroll-wrap {
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__empty-state {
    padding: $nebula-space-2x;
  }

  &--placeholder-hidden .vue-grid-placeholder {
    display: none;
  }
}
</style>
